import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, ExternalLink, Loader2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { TableHead } from "@/components/ui/table";
import {
  humanizeKey,
  type IssueSeverity,
  type QualityScore,
  type WritingQualityArticle,
  type WritingQualitySummary,
} from "../../../../../shared/index";
import { formatDate, formatScore, type SortDir, type WritingQualityFilters } from "./hooks";

export function FiltersBar({
  filters,
  setFilter,
  articles,
  onWriterChange,
}: {
  filters: WritingQualityFilters;
  setFilter: (key: keyof WritingQualityFilters, value: string) => void;
  /** Articles in the date range (before writer/action filters) — feeds the options. */
  articles: WritingQualityArticle[];
  onWriterChange?: (slug: string) => void;
}) {
  const writers = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of articles) map.set(a.authorSlug, a.authorName);
    return [...map].sort((a, b) => a[1].localeCompare(b[1]));
  }, [articles]);
  const actions = useMemo(
    () => [...new Set(articles.map((a) => a.recommendedAction))].sort(),
    [articles]
  );
  const selectClass = "h-9 w-full rounded-md border bg-background px-2 text-sm";

  return (
    <div className="grid gap-3 rounded-lg border bg-card p-4 sm:grid-cols-2 lg:grid-cols-4">
      <div className="space-y-2">
        <Label htmlFor="wq-from">From</Label>
        <Input
          id="wq-from"
          type="date"
          value={filters.from}
          max={filters.to}
          onChange={(e) => e.target.value && setFilter("from", e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="wq-to">To</Label>
        <Input
          id="wq-to"
          type="date"
          value={filters.to}
          min={filters.from}
          onChange={(e) => e.target.value && setFilter("to", e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="wq-writer">Writer</Label>
        <select
          id="wq-writer"
          className={selectClass}
          value={filters.writer}
          onChange={(e) =>
            onWriterChange ? onWriterChange(e.target.value) : setFilter("writer", e.target.value)
          }
        >
          <option value="">All writers</option>
          {filters.writer && !writers.some(([slug]) => slug === filters.writer) && (
            <option value={filters.writer}>{filters.writer}</option>
          )}
          {writers.map(([slug, name]) => (
            <option key={slug} value={slug}>
              {name}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="wq-action">Recommended action</Label>
        <select
          id="wq-action"
          className={selectClass}
          value={filters.action}
          onChange={(e) => setFilter("action", e.target.value)}
        >
          <option value="">All actions</option>
          {filters.action && !actions.includes(filters.action) && (
            <option value={filters.action}>{humanizeKey(filters.action)}</option>
          )}
          {actions.map((a) => (
            <option key={a} value={a}>
              {humanizeKey(a)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export function KpiCards({
  summary,
  showWriters = true,
}: {
  summary: WritingQualitySummary;
  showWriters?: boolean;
}) {
  const cards: { label: string; value: string; hint?: string }[] = [
    { label: "Articles evaluated", value: String(summary.articles) },
    { label: "Avg AI-Like Writing", value: formatScore(summary.avgAiWriting), hint: "0–100, higher = more AI-like" },
    { label: "Avg Editorial Quality", value: formatScore(summary.avgEditorialQuality), hint: "0–100, higher = better" },
  ];
  if (showWriters) cards.push({ label: "Writers evaluated", value: String(summary.writers) });
  return (
    <div className={`grid gap-4 sm:grid-cols-2 ${showWriters ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
      {cards.map((c) => (
        <div key={c.label} className="rounded-lg border bg-card p-4">
          <div className="text-sm text-muted-foreground">{c.label}</div>
          <div className="mt-1 text-2xl font-bold tabular-nums">{c.value}</div>
          {c.hint && <div className="mt-1 text-xs text-muted-foreground">{c.hint}</div>}
        </div>
      ))}
    </div>
  );
}

export function SortableHead<K>({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string;
  sortKey: K;
  sort: { key: K; dir: SortDir };
  onSort: (key: K) => void;
  className?: string;
}) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-foreground"
        onClick={() => onSort(sortKey)}
      >
        {label}
        <Icon className={`h-3.5 w-3.5 ${active ? "" : "opacity-40"}`} />
      </button>
    </TableHead>
  );
}

const ACTION_VARIANTS: Record<string, BadgeProps["variant"]> = {
  publish: "success",
  approve: "success",
  no_edit: "success",
  minor_edit: "secondary",
  major_edit: "warning",
  rewrite: "destructive",
  reject: "destructive",
};

export function ActionBadge({ action }: { action: string }) {
  return <Badge variant={ACTION_VARIANTS[action] ?? "outline"}>{humanizeKey(action)}</Badge>;
}

const SEVERITY_VARIANTS: Record<IssueSeverity, BadgeProps["variant"]> = {
  high: "destructive",
  medium: "warning",
  low: "secondary",
};

export function StatusBlock({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-center gap-2 p-12 text-sm text-muted-foreground">
      {children}
    </div>
  );
}

export function ErrorBlock({ error, fallback }: { error: unknown; fallback: string }) {
  return (
    <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
      {error instanceof ApiError ? error.message : fallback}
    </div>
  );
}

function ScoreBreakdown({ title, hint, score }: { title: string; hint: string; score: QualityScore }) {
  const dims = Object.entries(score.dimensions);
  // The dimensions sum to the total, so each is scored out of an equal share of max.
  const dimMax = dims.length ? score.max / dims.length : score.max;
  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-semibold">{title}</h3>
        <span className="text-2xl font-bold tabular-nums">
          {score.total}
          <span className="text-sm font-normal text-muted-foreground"> / {score.max}</span>
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>
      <ul className="mt-3 space-y-2">
        {dims.map(([key, value]) => (
          <li key={key} className="text-sm">
            <div className="flex justify-between">
              <span>{humanizeKey(key)}</span>
              <span className="tabular-nums text-muted-foreground">
                {value} / {dimMax}
              </span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-muted">
              <div
                className="h-1.5 rounded-full bg-primary"
                style={{ width: `${Math.min(100, (value / dimMax) * 100)}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ArticleEvaluationDialog({
  articleId,
  onClose,
}: {
  articleId: string | null;
  onClose: () => void;
}) {
  const query = useQuery({
    queryKey: ["writing-quality", "article", articleId],
    queryFn: () => api.writingQuality.article(articleId!),
    enabled: articleId !== null,
    staleTime: 5 * 60_000,
  });
  const article = query.data?.article;
  const ev = article?.evaluation;
  const recsByIssue = new Map<string, string[]>();
  for (const r of ev?.recommendations ?? []) {
    recsByIssue.set(r.issueId, [...(recsByIssue.get(r.issueId) ?? []), r.text]);
  }
  const issueIds = new Set((ev?.issues ?? []).map((i) => i.id));
  const looseRecs = (ev?.recommendations ?? []).filter((r) => !issueIds.has(r.issueId));

  return (
    <Dialog open={articleId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-4xl overflow-y-auto sm:max-w-4xl">
        {query.isLoading ? (
          <>
            <DialogTitle className="sr-only">Loading evaluation</DialogTitle>
            <StatusBlock>
              <Loader2 className="h-4 w-4 animate-spin" /> Loading evaluation…
            </StatusBlock>
          </>
        ) : query.isError || !article || !ev ? (
          <>
            <DialogTitle>Evaluation</DialogTitle>
            <ErrorBlock error={query.error} fallback="Failed to load the evaluation" />
          </>
        ) : (
          <div className="space-y-5">
            <div className="pr-8">
              <DialogTitle className="text-lg leading-snug">
                {article.url ? (
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:underline"
                  >
                    {article.title}
                    <ExternalLink className="ml-1.5 inline h-4 w-4 align-baseline" />
                  </a>
                ) : (
                  article.title
                )}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {article.authorName} · {formatDate(article.publishedAt)} · evaluated{" "}
                {formatDate(ev.meta.evaluatedAt)} ({ev.meta.model})
              </DialogDescription>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <ScoreBreakdown
                title="AI-Like Writing"
                hint="Higher = reads more AI-generated"
                score={ev.scores.aiWriting}
              />
              <ScoreBreakdown
                title="Editorial Quality"
                hint="Higher = stronger editorial quality"
                score={ev.scores.editorialQuality}
              />
            </div>

            <section className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold">Editorial assessment</h3>
                <span className="text-sm text-muted-foreground">Recommended action:</span>
                <ActionBadge action={ev.verdict.recommendedAction} />
              </div>
              <p className="text-sm leading-relaxed">{ev.verdict.summary}</p>
            </section>

            <section className="space-y-3">
              <h3 className="font-semibold">Flagged passages ({ev.issues.length})</h3>
              {ev.issues.length === 0 && (
                <p className="text-sm text-muted-foreground">No passages flagged.</p>
              )}
              {ev.issues.map((issue) => (
                <div key={issue.id} className="space-y-2 rounded-lg border p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={SEVERITY_VARIANTS[issue.severity] ?? "outline"}>
                      {humanizeKey(issue.severity)}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {humanizeKey(issue.dimension)}
                    </span>
                  </div>
                  <blockquote className="border-l-2 pl-3 text-sm italic">
                    “{issue.excerpt}”
                  </blockquote>
                  <p className="text-sm">{issue.description}</p>
                  {recsByIssue.get(issue.id)?.map((text, i) => (
                    <p key={i} className="rounded-md bg-muted p-2 text-sm">
                      <span className="font-medium">Recommendation: </span>
                      {text}
                    </p>
                  ))}
                </div>
              ))}
              {looseRecs.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Other recommendations</h4>
                  {looseRecs.map((r, i) => (
                    <p key={i} className="rounded-md bg-muted p-2 text-sm">
                      {r.text}
                    </p>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
